/**
 * Generate multiple split options for a ticket
 * Provides different ways to split jobs between technicians
 */
import { getProficiencyCategory } from '@/constants/proficiencyCategories';

interface Job {
  id: string;
  name: string;
  category: string;
  submenu: string;
  skill: number;
  minutes: number;
  bay: string;
  proficiency_category?: string;
}

interface Technician {
  id: string;
  name: string;
  proficiencies: Record<string, number>;
  skillTier: number;
  bayAssignments: string[];
}

export interface SplitOption {
  label: string;
  type: 'proficiency' | 'skill-level' | 'balanced';
  partA: {
    jobs: Job[];
    totalMinutes: number;
    avgSkillLevel: number;
    suggestedTechId: string | null;
    suggestedTechName: string | null;
    proficiencyMatch: number;
    categories: string[];
    scheduledStart?: string; // HH:MM format
    scheduledFinish?: string; // HH:MM format
    wallMinutes?: number;
    dueOutViolation?: boolean; // Flag for when finish time exceeds due-out
  };
  partB: {
    jobs: Job[];
    totalMinutes: number;
    avgSkillLevel: number;
    suggestedTechId: string | null;
    suggestedTechName: string | null;
    proficiencyMatch: number;
    categories: string[];
    scheduledStart?: string; // HH:MM format
    scheduledFinish?: string; // HH:MM format
    wallMinutes?: number;
    dueOutViolation?: boolean; // Flag for when finish time exceeds due-out
  };
  reasoning: string;
}

/**
 * Normalize bay type names for matching
 * Maps specific bay names to generic types
 */
function normalizeBayType(bayName: string): string {
  if (!bayName) return '';
  const normalized = bayName.toLowerCase().trim();
  
  // "Any" (or similar) means no specific requirement
  if (normalized === 'any' || normalized === 'either' || normalized === 'none') return 'any';
  
  // Map all lift variations to "lift"
  if (normalized.includes('lift')) return 'lift';
  
  // Map alignment variations
  if (normalized.includes('alignment')) return 'alignment';
  
  // Map flat variations
  if (normalized.includes('flat') || normalized.includes('drive-through')) return 'flat';
  
  // Map specialty variations
  if (normalized.includes('specialty')) return 'specialty';
  
  // Return original if no match (lowercase for comparison)
  return normalized;
}

/**
 * Calculate average proficiency for a set of jobs
 */
function calculateAvgProficiency(jobs: Job[], techProficiencies: Record<string, number>): number {
  if (jobs.length === 0) return 0;
  
  const totalProf = jobs.reduce((sum, job) => {
    // Resolved through the canonical contract. This read used the raw
    // catalogue value, so a job keyed 'brakes' scored 0 against a store keyed
    // 'Brakes' and splitting saw no proficiency at all.
    const category = getProficiencyCategory(job.proficiency_category || job.category);
    return sum + (techProficiencies[category] || 0);
  }, 0);
  
  return totalProf / jobs.length;
}

/**
 * Find best technician for a group of jobs
 */
function findBestTech(
  jobs: Job[], 
  technicians: Technician[]
): { id: string; name: string; proficiency: number } | null {
  let bestTech = null;
  let bestProf = -1;
  
  for (const tech of technicians) {
    const avgProf = calculateAvgProficiency(jobs, tech.proficiencies);
    const maxSkill = Math.max(...jobs.map(j => j.skill));
    
    // Skip if tech doesn't meet skill requirements (tech must be at or above job skill level)
    if (maxSkill > tech.skillTier) continue;
    
    // Check bay compatibility using normalized types
    // NOTE: Lift ⊇ flat — flat-OK jobs are allowed in lift bays.
    // Lift-required jobs MUST NOT go to flat-only techs (hard constraint).
    // Technician concurrency is allowed (multi-bay); we do NOT enforce one-ticket-per-tech.
    const rawRequired = jobs.map(j => normalizeBayType(j.bay));
    const requiredBays = [...new Set(rawRequired.filter(b => b && b !== 'any'))];
    const techBays = (tech.bayAssignments || []).map(bay => normalizeBayType(bay));
    
    // If no specific bay is required (empty after filtering) → bypass bay check
    const hasBays = requiredBays.length === 0 || requiredBays.every(reqBay => techBays.includes(reqBay));
    
    if (!hasBays) {
      console.log(`Tech ${tech.name} bay mismatch - needs: [${requiredBays.join(', ')}], has: [${techBays.join(', ')}]`);
      continue;
    }
    
    if (avgProf > bestProf) {
      bestProf = avgProf;
      bestTech = { id: tech.id, name: tech.name, proficiency: avgProf };
    }
  }
  
  return bestTech;
}

/**
 * Create proficiency-based split option
 */
function createProficiencySplit(
  jobs: Job[],
  technicians: Technician[]
): SplitOption | null {
  // Group by proficiency category
  const categoryGroups = new Map<string, Job[]>();
  
  jobs.forEach(job => {
    const category = job.proficiency_category || job.category;
    if (!categoryGroups.has(category)) {
      categoryGroups.set(category, []);
    }
    categoryGroups.get(category)!.push(job);
  });
  
  if (categoryGroups.size < 2) return null;
  
  // Sort categories by job count, assign largest to partA
  const sortedCategories = Array.from(categoryGroups.entries())
    .sort((a, b) => b[1].length - a[1].length);
  
  // Part A gets the largest category, Part B gets everything else
  const cat1Jobs = sortedCategories[0][1];
  const cat2Jobs = sortedCategories.slice(1).flatMap(([_, jobs]) => jobs);
  
  const bestTech1 = findBestTech(cat1Jobs, technicians);
  const bestTech2 = findBestTech(cat2Jobs, technicians);
  
  return {
    label: 'Proficiency-Based Split',
    type: 'proficiency',
    partA: {
      jobs: cat1Jobs,
      totalMinutes: cat1Jobs.reduce((sum, j) => sum + j.minutes, 0),
      avgSkillLevel: Math.round(cat1Jobs.reduce((sum, j) => sum + j.skill, 0) / cat1Jobs.length),
      suggestedTechId: bestTech1?.id || null,
      suggestedTechName: bestTech1?.name || null,
      proficiencyMatch: bestTech1?.proficiency || 0,
      categories: [...new Set(cat1Jobs.map(j => j.proficiency_category || j.category))],
    },
    partB: {
      jobs: cat2Jobs,
      totalMinutes: cat2Jobs.reduce((sum, j) => sum + j.minutes, 0),
      avgSkillLevel: Math.round(cat2Jobs.reduce((sum, j) => sum + j.skill, 0) / cat2Jobs.length),
      suggestedTechId: bestTech2?.id || null,
      suggestedTechName: bestTech2?.name || null,
      proficiencyMatch: bestTech2?.proficiency || 0,
      categories: [...new Set(cat2Jobs.map(j => j.proficiency_category || j.category))],
    },
    reasoning: `Split by specialist categories: ${sortedCategories[0][0]} vs others`,
  };
}

/**
 * Create skill-level based split option
 */
function createSkillLevelSplit(
  jobs: Job[],
  technicians: Technician[]
): SplitOption | null {
  const sortedJobs = [...jobs].sort((a, b) => b.skill - a.skill);
  const avgSkill = jobs.reduce((sum, j) => sum + j.skill, 0) / jobs.length;
  
  const highSkillJobs = sortedJobs.filter(j => j.skill >= avgSkill);
  const lowSkillJobs = sortedJobs.filter(j => j.skill < avgSkill);
  
  if (highSkillJobs.length === 0 || lowSkillJobs.length === 0) {
    // Just split in half
    const midpoint = Math.floor(sortedJobs.length / 2);
    const group1 = sortedJobs.slice(0, midpoint);
    const group2 = sortedJobs.slice(midpoint);
    
    const bestTech1 = findBestTech(group1, technicians);
    const bestTech2 = findBestTech(group2, technicians);
    
    return {
      label: 'Skill-Level Split',
      type: 'skill-level',
      partA: {
        jobs: group1,
        totalMinutes: group1.reduce((sum, j) => sum + j.minutes, 0),
        avgSkillLevel: Math.round(group1.reduce((sum, j) => sum + j.skill, 0) / group1.length),
        suggestedTechId: bestTech1?.id || null,
        suggestedTechName: bestTech1?.name || null,
        proficiencyMatch: bestTech1?.proficiency || 0,
        categories: [...new Set(group1.map(j => j.proficiency_category || j.category))],
      },
      partB: {
        jobs: group2,
        totalMinutes: group2.reduce((sum, j) => sum + j.minutes, 0),
        avgSkillLevel: Math.round(group2.reduce((sum, j) => sum + j.skill, 0) / group2.length),
        suggestedTechId: bestTech2?.id || null,
        suggestedTechName: bestTech2?.name || null,
        proficiencyMatch: bestTech2?.proficiency || 0,
        categories: [...new Set(group2.map(j => j.proficiency_category || j.category))],
      },
      reasoning: 'Split by skill complexity (advanced vs standard work)',
    };
  }
  
  const bestTech1 = findBestTech(highSkillJobs, technicians);
  const bestTech2 = findBestTech(lowSkillJobs, technicians);
  
  return {
    label: 'Skill-Level Split',
    type: 'skill-level',
    partA: {
      jobs: highSkillJobs,
      totalMinutes: highSkillJobs.reduce((sum, j) => sum + j.minutes, 0),
      avgSkillLevel: Math.round(highSkillJobs.reduce((sum, j) => sum + j.skill, 0) / highSkillJobs.length),
      suggestedTechId: bestTech1?.id || null,
      suggestedTechName: bestTech1?.name || null,
      proficiencyMatch: bestTech1?.proficiency || 0,
      categories: [...new Set(highSkillJobs.map(j => j.proficiency_category || j.category))],
    },
    partB: {
      jobs: lowSkillJobs,
      totalMinutes: lowSkillJobs.reduce((sum, j) => sum + j.minutes, 0),
      avgSkillLevel: Math.round(lowSkillJobs.reduce((sum, j) => sum + j.skill, 0) / lowSkillJobs.length),
      suggestedTechId: bestTech2?.id || null,
      suggestedTechName: bestTech2?.name || null,
      proficiencyMatch: bestTech2?.proficiency || 0,
      categories: [...new Set(lowSkillJobs.map(j => j.proficiency_category || j.category))],
    },
    reasoning: `Split by skill level (L${Math.max(...highSkillJobs.map(j => j.skill))} vs L${Math.max(...lowSkillJobs.map(j => j.skill))})`,
  };
}

/**
 * Create balanced time split option
 */
function createBalancedTimeSplit(
  jobs: Job[],
  technicians: Technician[]
): SplitOption {
  // Sort jobs by minutes (longest first)
  const sortedJobs = [...jobs].sort((a, b) => b.minutes - a.minutes);
  
  // Use greedy algorithm to balance minutes
  const group1: Job[] = [];
  const group2: Job[] = [];
  let group1Minutes = 0;
  let group2Minutes = 0;
  
  for (const job of sortedJobs) {
    if (group1Minutes <= group2Minutes) {
      group1.push(job);
      group1Minutes += job.minutes;
    } else {
      group2.push(job);
      group2Minutes += job.minutes;
    }
  }
  
  const bestTech1 = findBestTech(group1, technicians);
  const bestTech2 = findBestTech(group2, technicians);
  
  return {
    label: 'Balanced Time Split',
    type: 'balanced',
    partA: {
      jobs: group1,
      totalMinutes: group1Minutes,
      avgSkillLevel: Math.round(group1.reduce((sum, j) => sum + j.skill, 0) / group1.length),
      suggestedTechId: bestTech1?.id || null,
      suggestedTechName: bestTech1?.name || null,
      proficiencyMatch: bestTech1?.proficiency || 0,
      categories: [...new Set(group1.map(j => j.proficiency_category || j.category))],
    },
    partB: {
      jobs: group2,
      totalMinutes: group2Minutes,
      avgSkillLevel: Math.round(group2.reduce((sum, j) => sum + j.skill, 0) / group2.length),
      suggestedTechId: bestTech2?.id || null,
      suggestedTechName: bestTech2?.name || null,
      proficiencyMatch: bestTech2?.proficiency || 0,
      categories: [...new Set(group2.map(j => j.proficiency_category || j.category))],
    },
    reasoning: `Equal workload distribution (${group1Minutes} min vs ${group2Minutes} min)`,
  };
}

/**
 * Check if two split options are identical
 */
function areOptionsIdentical(opt1: SplitOption, opt2: SplitOption): boolean {
  // Compare suggested technicians
  if (opt1.partA.suggestedTechId !== opt2.partA.suggestedTechId) return false;
  if (opt1.partB.suggestedTechId !== opt2.partB.suggestedTechId) return false;
  
  // Compare job distributions (by job IDs)
  const jobs1A = opt1.partA.jobs.map(j => j.id).sort().join(',');
  const jobs1B = opt1.partB.jobs.map(j => j.id).sort().join(',');
  const jobs2A = opt2.partA.jobs.map(j => j.id).sort().join(',');
  const jobs2B = opt2.partB.jobs.map(j => j.id).sort().join(',');
  
  return jobs1A === jobs2A && jobs1B === jobs2B;
}

/**
 * Generate all applicable split options for a ticket
 */
export function generateSplitOptions(
  jobs: Job[],
  technicians: Technician[],
  proficiencyGap: number,
  skillGap: number,
  splitParentId?: string | null
): SplitOption[] {
  // GUARD: Never split tickets that are already split children
  if (splitParentId) {
    console.log('[generateSplitOptions] Ticket is already a split child - skipping split generation');
    return [];
  }
  
  const options: SplitOption[] = [];
  
  // Option 1: Proficiency-based (if gap >= 50%)
  if (proficiencyGap >= 50) {
    const profOption = createProficiencySplit(jobs, technicians);
    if (profOption) options.push(profOption);
  }
  
  // Option 2: Skill-level based (if gap >= 2)
  if (skillGap >= 2) {
    const skillOption = createSkillLevelSplit(jobs, technicians);
    if (skillOption) options.push(skillOption);
  }
  
  // Option 3: Balanced time (always available)
  const balancedOption = createBalancedTimeSplit(jobs, technicians);
  options.push(balancedOption);
  
  // Filter out duplicate options AND options where both parts go to same technician
  const uniqueOptions: SplitOption[] = [];
  for (const option of options) {
    // CRITICAL: Reject splits where both parts go to same technician - no operational benefit
    if (option.partA.suggestedTechId && 
        option.partA.suggestedTechId === option.partB.suggestedTechId) {
      console.log(`[generateSplitOptions] Rejecting ${option.label}: both parts would go to ${option.partA.suggestedTechName}`);
      continue;
    }
    
    const isDuplicate = uniqueOptions.some(existing => areOptionsIdentical(option, existing));
    if (!isDuplicate) {
      uniqueOptions.push(option);
    }
  }
  
  return uniqueOptions;
}
